// =============================================================================
// ApReceiptsCardsScreen — /ap/receipts (T-331). Receipts to code, bank-rec
// style: a scroll list of slim CodingCards, AI pre-filled. High-confidence
// cards tint GREEN (approve fast); lower-confidence tint amber (needs your
// eyes). Lookups load once and pass down so N cards don't fire N×5 queries.
// =============================================================================

import { Link } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Upload, Receipt } from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Button, Spinner, EmptyState } from "@/components/ui";
import CodingCard, { ApLookups } from "./CodingCard";

const GREEN_AT = 0.85;

export default function ApReceiptsCardsScreen() {
  useRequireAuth();

  const { data: lookups } = useQuery({
    queryKey: ["ap_card_lookups"],
    queryFn: async (): Promise<ApLookups> => {
      const [sup, prj, bud, crd, brd] = await Promise.all([
        supabase.from("suppliers").select("supplier_id, legal_name, abn").eq("is_active", true).order("legal_name"),
        supabase.from("projects").select("project_id, project_code, project_name, site:site_id ( site_name )").not("status", "in", "(closed,cancelled)").order("project_code", { ascending: false }).limit(200),
        supabase.from("budget_codes").select("budget_code, description:display_name").eq("is_active", true).order("sort_order"),
        supabase.from("card_register").select("card_id, last4, label, card_type, holder_employee_id, xero_account_name").eq("is_active", true).order("last4"),
        supabase.from("brand_map").select("brand_id, brand_display_name").order("brand_display_name"),
      ]);
      return { suppliers: sup.data ?? [], projects: prj.data ?? [], budgetCodes: bud.data ?? [], cards: crd.data ?? [], brands: brd.data ?? [] };
    },
  });

  const { data: receipts = [], isLoading, refetch } = useQuery({
    queryKey: ["ap_receipts_cards"],
    queryFn: async () => {
      const { data } = await supabase
        .from("pending_receipts")
        .select("receipt_id, ocr_raw_json, ocr_confidence, ocr_processed_at, ocr_attempts, ocr_last_error, storage_path, mime_type, supplier_guess, amount_guess, project_id, captured_at, notes, claimed_personal, no_tax_invoice, source_statement_receipt_id, captured_by_employee_id, capturer:employees!pending_receipts_captured_by_employee_id_fkey(first_name, last_name)")
        .eq("status", "new")
        .order("ocr_confidence", { ascending: false, nullsFirst: false });
      const rows = (data ?? []) as any[];
      // Sign all images in one pass.
      await Promise.all(rows.map(async (r) => {
        if (!r.storage_path) return;
        const { data: s } = await supabase.storage.from("receipt-captures").createSignedUrl(r.storage_path, 3600);
        r._url = s?.signedUrl ?? null;
      }));

      // Duplicate flagging (same idea as Push-to-Xero: look twice, never block).
      // (a) within this queue — two pending receipts, same supplier + same $;
      // (b) against recent AP invoices — this receipt may already be coded.
      const { data: recent } = await supabase
        .from("ap_invoices")
        .select("supplier_name, amount_inc_gst")
        .neq("status", "void")
        .order("captured_at", { ascending: false })
        .limit(400);
      const key = (s: string | null, a: number | null) =>
        `${(s ?? "").trim().toLowerCase()}|${Number(a ?? 0).toFixed(2)}`;
      const inQueue = new Map<string, number>();
      for (const r of rows) inQueue.set(key(r.supplier_guess, r.amount_guess), (inQueue.get(key(r.supplier_guess, r.amount_guess)) ?? 0) + 1);
      const coded = new Set((recent ?? []).map((i: any) => key(i.supplier_name, i.amount_inc_gst)));
      for (const r of rows) {
        const k = key(r.supplier_guess, r.amount_guess);
        const hasAmount = Number(r.amount_guess ?? 0) > 0;
        r._dupQueue = hasAmount && (inQueue.get(k) ?? 0) > 1;
        r._dupCoded = hasAmount && coded.has(k);
      }
      return rows;
    },
  });

  const greens = (receipts as any[]).filter((r) => (r.ocr_confidence ?? 0) >= GREEN_AT).length;

  return (
    // James, 26 Aug, first look at the new per-line tax controls: "getting a
    // little busy - make the panel a little wider to use the space." 3xl was
    // sized for the pre-lines card; the split rows earn the room.
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <Link to="/ap" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"><ArrowLeft size={14} /> AP / Spend</Link>
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Receipts to code</h1>
          <div className="text-text-muted text-[13px] mt-1">
            {receipts.length} waiting{greens > 0 && <> · <span className="text-ok">{greens} ready (green)</span></>}. The AI's pre-filled what it can — fill blanks, approve.
          </div>
        </div>
        <Link to="/ap/intake"><Button variant="secondary" icon={Upload}>Bulk upload</Button></Link>
      </div>

      {isLoading || !lookups ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : receipts.length === 0 ? (
        <EmptyState icon={Receipt} title="All caught up" description="No receipts waiting to code. Bulk upload or snap one to get started." />
      ) : (
        <div className="space-y-3">
          {(receipts as any[]).map((r) => {
            const conf = r.ocr_confidence ?? null;
            const green = conf != null && conf >= GREEN_AT;
            const amber = conf != null && conf < GREEN_AT;
            const tint = green ? "bg-ok/10 border-ok/55" : amber ? "bg-warn/10 border-warn/55" : "bg-ink-card border-line";
            const capturerName = [r.capturer?.first_name, r.capturer?.last_name].filter(Boolean).join(" ");
            // Channel: bulk office intake stamps a "Bulk intake — …" note;
            // anything else came off a phone via the field snap flow.
            const channel = (r.notes ?? "").startsWith("Bulk intake") ? "bulk upload" : "phone snap";
            const isDup = r._dupQueue || r._dupCoded;
            return (
              <div key={r.receipt_id} className={`border rounded-lg ${isDup ? "border-danger/60" : ""} ${tint}`}>
                <div className="px-4 pt-2.5 flex items-center justify-between gap-2 flex-wrap">
                  {/* Provenance — who loaded it + how it got here, front and centre */}
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-ink-raised border border-line text-[12px] text-text">
                    <span className="font-medium">{capturerName || "Unknown uploader"}</span>
                    <span className="text-text-muted">· {channel}{r.captured_at ? ` · ${new Date(r.captured_at).toLocaleDateString()}` : ""}</span>
                  </span>
                  <span className="flex items-center gap-2 text-[11px]">
                    {r.claimed_personal && (
                      <span className="px-1.5 py-0.5 rounded bg-warn/15 text-warn border border-warn/40 font-semibold">
                        PERSONAL SPEND — reimburse {capturerName || "the capturer"}
                      </span>
                    )}
                    {r._dupQueue && (
                      <span className="px-1.5 py-0.5 rounded bg-danger/15 text-danger border border-danger/40 font-medium">
                        possible dup — same supplier + $ twice in this queue
                      </span>
                    )}
                    {!r._dupQueue && r._dupCoded && (
                      <span className="px-1.5 py-0.5 rounded bg-danger/15 text-danger border border-danger/40 font-medium">
                        possible dup — matching bill already coded
                      </span>
                    )}
                    {r.ocr_processed_at
                      ? <span className="uppercase tracking-wider2 text-text-muted">AI read</span>
                      : <AiNotRun receipt={r} onDone={() => refetch()} />}
                    {conf != null && <span className={green ? "text-ok" : "text-warn"}>{Math.round(conf * 100)}%</span>}
                  </span>
                </div>
                <CodingCard receipt={r} imageUrl={r._url} lookups={lookups} onDone={() => refetch()} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// 8 Oct 2026 — "AI NOT RUN" now says why and offers to run it here, instead of
// sending James to the old Pending-receipts screen. The worker records every
// attempt and its reason (mig 424); the 15-minute sweep retries on its own, so
// this button is for "I'm looking at it now".
function AiNotRun({ receipt, onDone }: { receipt: any; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const attempts = Number(receipt.ocr_attempts ?? 0);
  const why = receipt.ocr_last_error
    ? `last attempt: ${receipt.ocr_last_error}`
    : attempts === 0 ? "the phone's call never reached the worker — the sweep retries within 15 min" : `${attempts} attempt${attempts === 1 ? "" : "s"}, no reason recorded`;
  async function run() {
    setBusy(true); setMsg(null);
    try {
      const res = await apiFetch("/ai/extract-document", { method: "POST", body: JSON.stringify({ pending_receipt_id: receipt.receipt_id }) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMsg(`AI failed: ${err.detail ?? err.error ?? res.status}`);
      } else {
        onDone();
      }
    } catch (e: any) {
      setMsg(`AI call error: ${e?.message ?? e}`);
    } finally { setBusy(false); }
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span className="uppercase tracking-wider2 text-warn" title={why}>AI not run</span>
      <button type="button" onClick={run} disabled={busy}
              className="px-2 py-0.5 rounded border border-accent text-accent hover:bg-accent hover:text-white disabled:opacity-50 text-[11px] font-medium">
        {busy ? "Reading…" : "Run AI now"}
      </button>
      {msg && <span className="text-danger normal-case tracking-normal">{msg}</span>}
    </span>
  );
}
